import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Select, Input, Row, Col } from 'antd';
import { SwitchCommon, ButtonCommon, ErrorMessage, PrefixedInput, showAlertNotification } from 'components/common';
import { BaseRecord, AddUnitModalProps, UNIT_TYPE } from 'types';
const { TextArea } = Input;

const AddUnitModal: React.FC<AddUnitModalProps> = ({
  isOpen,
  activeTab,
  onCancel,
  onSubmit,
  onGenerate,
  form,
  editMode = false,
  unitData = null,
  isSubmitting = false,
  isCopyMode = false,
}) => {
  const [isActive, setIsActive] = useState<boolean>(true);
  const [modalOpened, setModalOpened] = useState(false);
  const [previousUnitCategory, setPreviousUnitCategory] = useState<string>('');
  const [codePrefix, setCodePrefix] = useState<string>('');
  const [genCode, setGenCode] = useState<string>('');
  const handleToggleActive = useCallback(
    (checked: boolean) => {
      setIsActive(checked);
      form.setFieldValue('isActive', checked);
    },
    [form],
  );
  useEffect(() => {
    if (genCode) {
      const prefix = genCode.substring(0, 6);
      setCodePrefix(prefix);
      form.setFieldsValue({ code: genCode });
    }
    if (unitData?.code) {
      const prefix = unitData.code.substring(0, 6);
      setCodePrefix(prefix);
    }
  }, [unitData?.code, genCode]);
  useEffect(() => {
    const fetchCode = async () => {
      if (isOpen && !modalOpened) {
        setModalOpened(true);

        if (!editMode) {
          const code = await onGenerate();
          setGenCode(code);
        }
      }

      if (!isOpen) {
        setModalOpened(false);
      }
    };
    fetchCode();
  }, [isOpen, editMode, onGenerate, modalOpened]);
  const handleUnitCategoryChange = async (newCategory: string) => {
    if (!editMode && newCategory && newCategory !== previousUnitCategory) {
      setPreviousUnitCategory(newCategory);

      const code = await onGenerate();
      setGenCode(code);
      setCodePrefix(code.substring(0, 6));
      form.setFieldsValue({ code });
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (editMode && unitData) {
        setIsActive(unitData.isActive);
        form.setFieldsValue({
          name: unitData.name,
          code: unitData.code,
          unitCategory: unitData.type as string,
          isActive: unitData.isActive,
          description: unitData.description as string,
        });
      } else if (!editMode && !isCopyMode) {
        form.resetFields();
        setIsActive(true);
        setPreviousUnitCategory(activeTab);
        form.setFieldsValue({
          unitCategory: activeTab as string,
          isActive: true,
        });
      }
    }
  }, [isOpen, form, editMode, unitData, activeTab, isCopyMode]);

  const HiddenFormItems = () => (
    <Form.Item name='isActive' hidden initialValue={true}>
      <Input />
    </Form.Item>
  );

  const getModalTitle = () => {
    if (editMode) {
      return 'CHỈNH SỬA ĐƠN VỊ';
    }
    return 'TẠO ĐƠN VỊ MỚI';
  };

  const getModalDescription = () => {
    if (editMode) {
      return 'Vui lòng chỉnh sửa thông tin đơn vị theo nhu cầu của bạn';
    }
    return 'Vui lòng điền đầy đủ thông tin của danh mục trước khi thêm đơn vị mới';
  };

  const getSubmitButtonText = () => {
    if (editMode) {
      return 'Cập nhật';
    }
    return 'Thêm đơn vị';
  };

  return (
    <Modal
      title={
        <div className='flex flex-col bg-white py-[12px] px-[24px] rounded-lg gap-2'>
          <h2 className='text-[px] font-bold leading-[24px]'>{getModalTitle()}</h2>
          <p className='text-[14px] leading-[20px] text-[var(--textBlack56)] font-normal'>{getModalDescription()}</p>
        </div>
      }
      open={isOpen}
      // onCancel={onCancel}
      footer={null}
      width={1255}
      height={595}
      centered
      className='unit-modal'
      closeIcon={null}
      styles={{
        wrapper: {
          alignItems: 'center',
        },
      }}
      maskClosable={false}
    >
      <div className='px-[24px] py-[12] mb-[12px]'>
        <div className='flex-1 flex flex-col gap-1 p-4 max-h-[calc(100vh-250px)] overflow-y-auto bg-white rounded-lg '>
          <Form
            form={form}
            requiredMark={false}
            layout='vertical'
            className='bg-white rounded-lg py-[24px] px-[48px] font-medium text-[14px] leading-[20px]'
          >
            <HiddenFormItems />
            {/* <div className='flex items-start space-x-4'> */}
            <Row gutter={24}>
              <Col xl={8} lg={14} md={12} xs={24}>
                <Form.Item
                  label={
                    <span className='relative'>
                      Tên loại đơn vị
                      <span className=' absolute top-0.5 -right-3.5 text-red-500 text-[8px] font-bold'>✱</span>
                    </span>
                  }
                  name='name'
                  // className=' w-[420px]'
                  rules={[
                    {
                      required: true,
                      message: <ErrorMessage message='Giá trị này là bắt buộc, bạn phải nhập giá trị!' />,
                    },
                    { max: 100, message: <ErrorMessage message='Tên đơn vị không được vượt quá 100 ký tự!' /> },
                  ]}
                >
                  <Input placeholder='Nhập vào tên loại đơn vị' />
                </Form.Item>
              </Col>
              <Col xl={6} lg={10} md={12} xs={24}>
                <Form.Item
                  label={
                    <span className='relative'>
                      Mã đơn vị{' '}
                      <span className=' absolute top-0.5 -right-3.5 text-red-500 text-[8px] font-bold'>✱</span>
                    </span>
                  }
                  name='code'
                  // className=' w-[228px]'
                  rules={[
                    { required: true, message: <ErrorMessage message='Vui lòng nhập mã đơn vị!' /> },
                    { max: 20, message: <ErrorMessage message='Mã đơn vị không được vượt quá 10 ký tự!' /> },
                    {
                      pattern: /^[a-zA-Z0-9_]+$/,
                      message: <ErrorMessage message='Mã đơn vị chỉ được chứa chữ cái, số và dấu gạch dưới!' />,
                    },
                  ]}
                >
                  <PrefixedInput
                    prefix={codePrefix}
                    maxEditableLength={6}
                    placeholder='Nhập mã'
                    disabled={editMode && !isCopyMode}
                    className='!text-[14px] !leading-[20px] h-[32px]'
                    inputProps={{
                      suffix: (!editMode || isCopyMode) && (
                        <span
                          className='text-[var(--primaryColor500)] cursor-pointer'
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (onGenerate) {
                              try {
                                const code = await onGenerate();
                                if (code && code.length >= 6) {
                                  setCodePrefix(code.substring(0, 6));
                                  form.setFieldsValue({ code });
                                }
                              } catch (error) {
                                console.error('Error generating code:', error);
                              }
                            }
                          }}
                        >
                          Generate
                        </span>
                      ),
                      hardSize: 'sm',
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xl={6} lg={14} md={12} xs={24}>
                <Form.Item
                  label={
                    <span className='relative'>
                      Loại đơn vị{' '}
                      <span className=' absolute top-0.5 -right-3.5 text-red-500 text-[8px] font-bold'>✱</span>
                    </span>
                  }
                  name='unitCategory'
                  initialValue={activeTab}
                  // className='w-[228px]'
                  rules={[
                    {
                      required: true,
                      message: <ErrorMessage message='Giá trị này là bắt buộc, bạn phải chọn giá trị!' />,
                    },
                  ]}
                >
                  <Select
                    placeholder='Chọn loại đơn vị'
                    disabled={editMode || isCopyMode}
                    onChange={handleUnitCategoryChange}
                  >
                    <Select.Option value={UNIT_TYPE.Currency}>Tiền tệ</Select.Option>
                    <Select.Option value={UNIT_TYPE.Unit}>Sản phẩm</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xl={4} lg={10} md={12} xs={24}>
                <div className='mt-8 flex items-center ml-4'>
                  <SwitchCommon
                    disabled={editMode}
                    label={isActive ? 'Kích hoạt' : 'Chưa kích hoạt'}
                    isActive={isActive}
                    record={{} as BaseRecord<unknown>}
                    onToggle={handleToggleActive}
                  />
                </div>
              </Col>
            </Row>

            {/* </div> */}

            <Form.Item
              className='font-normal'
              label='Mô tả đơn vị'
              name='description'
              rules={[{ max: 1000, message: <ErrorMessage message='Mô tả không được vượt quá 1000 ký tự!' /> }]}
            >
              <TextArea rows={8} placeholder='Nhập vào mô tả...' className='resize-none' />
            </Form.Item>
          </Form>
        </div>
      </div>
      <div className='flex justify-center gap-3 mt-6'>
        <ButtonCommon color='none' size='md' radius='rounded' onClick={onCancel} disabled={isSubmitting}>
          Hủy
        </ButtonCommon>
        <ButtonCommon
          color='primary'
          size='md'
          radius='rounded'
          loading={isSubmitting}
          onClick={() => {
            form
              .validateFields()
              .then(onSubmit)
              .catch((errorInfo) => {
                showAlertNotification({
                  type: 'warning',
                  title: 'Thông báo',
                  message: 'Vui lòng nhập đúng thông tin thanh toán',
                });
                console.log('Validation failed:', errorInfo);
              });
          }}
        >
          {getSubmitButtonText()}
        </ButtonCommon>
      </div>
    </Modal>
  );
};

export default AddUnitModal;
